import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  Area,
  Bar,
  Line,
} from "recharts";
import { Maximize2, X, ZoomIn, ZoomOut, RotateCcw, BarChart3 } from "lucide-react";
import {
  fmt$, fmtPnL, fmtPnLPct, buildTooltip, mergeCurves,
} from "./payoff/utils";
import { ScenarioSliders } from "./payoff/ScenarioSliders";
import {
  InteractiveLegOverlay,
  LegPickerPopover,
  DragLivePill,
} from "./payoff/InteractiveLegOverlay";

export const PayoffChart = ({
  curve = [],
  todayCurve = [],
  tnCurves = null,
  cone = null,
  spot,
  breakevens = [],
  maxProfit,
  maxLoss,
  pop,
  vega = null,         // total strategy vega (per 1 vol-point), powers IV shock approx
  netPremium = null,   // signed net entry $ (debit positive). Fallback risk denominator.
  volumeByStrike = null,
  volumeOverlay = null,
  // Interactive editing (OptionStrat-style click-to-add + drag-strike).
  interactive = false,
  legs = [],
  listedStrikes = null,
  onAddLeg = null,
  onLegStrikeChange = null,
  onLegRemove = null,
  // Optional controlled scenario state. When provided, the inline scenario
  // panel is hidden — assume the parent owns those controls.
  externalTimeFrac = null,
  externalShockPct = null,
  externalIvShockVp = null,
  setExternalTimeFrac = null,
  setExternalShockPct = null,
  setExternalIvShockVp = null,
  hideInlineScenario = false,
  // Optional external override for the dollar/percent toggle. When set,
  // the parent (ForgePage view tabs) owns the mode and the in-chart toggle
  // is hidden so users don't see two competing controls.
  externalPnlMode = null,
}) => {
  const [showCone, setShowCone] = useState(true);
  const [showToday, setShowToday] = useState(true);
  const [showVolume, setShowVolume] = useState(true);
  const [showProb, setShowProb] = useState(true);   // bell-curve overlay
  const [internalPnlMode, setInternalPnlMode] = useState("dollars"); // 'dollars' | 'percent'
  const pnlMode = externalPnlMode || internalPnlMode;
  const setPnlMode = externalPnlMode ? () => {} : setInternalPnlMode;
  const [pickerAt, setPickerAt] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const draggingLegRef = useRef(null);
  const [dragLive, setDragLive] = useState(null);
  const [snapToListed, setSnapToListed] = useState(true);
  const [focusedLegIdx, setFocusedLegIdx] = useState(null);
  // OptionStrat-style scenario sliders — `timeFrac` blends between today (0)
  // and expiry (1) using the backend's pre-computed T+N snapshots;
  // `shockPct` shows a vertical "what if spot moves ±X%" marker with the
  // P/L readout pulled from whichever curve is active. `ivShockVp` shifts
  // the today curve by `vega * ivShockVp` to approximate a vol crush/spike.
  //
  // Controlled mode: when the parent passes externalX values, those win.
  const [internalTimeFrac, setInternalTimeFrac] = useState(0);
  const [internalShockPct, setInternalShockPct] = useState(0);
  const [internalIvShockVp, setInternalIvShockVp] = useState(0);
  const timeFrac    = externalTimeFrac    != null ? externalTimeFrac    : internalTimeFrac;
  const shockPct    = externalShockPct    != null ? externalShockPct    : internalShockPct;
  const ivShockVp   = externalIvShockVp   != null ? externalIvShockVp   : internalIvShockVp;
  const setTimeFrac    = setExternalTimeFrac    || setInternalTimeFrac;
  const setShockPct    = setExternalShockPct    || setInternalShockPct;
  const setIvShockVp   = setExternalIvShockVp   || setInternalIvShockVp;
  // Derive a {strike: volume} map from either the new `volumeOverlay.bars`
  // shape (backend), or the legacy `volumeByStrike` prop, whichever is set.
  const volMap = useMemo(() => {
    if (volumeByStrike) return volumeByStrike;
    if (Array.isArray(volumeOverlay?.bars)) {
      const m = {};
      volumeOverlay.bars.forEach((b) => { m[b.strike] = b.volume; });
      return m;
    }
    return null;
  }, [volumeByStrike, volumeOverlay]);
  const hasVolume = !!(volMap && Object.keys(volMap).length);
  // Zoom state — null means "auto" (full data range). When the user wheels
  // or clicks +/− we lock to an explicit [min,max] domain.
  const [xDomain, setXDomain] = useState(null);
  const hoverPriceRef = useRef(null);
  const wheelHostRef = useRef(null);
  const fsWheelHostRef = useRef(null);
  // Active "today" curve — interpolated between adjacent T+N snapshots based
  // on the time slider. When tnCurves isn't provided, falls back to the raw
  // `todayCurve` prop (legacy behavior).
  const activeTodayCurve = useMemo(() => {
    if (!tnCurves || !tnCurves.length) return todayCurve || [];
    if (timeFrac <= 0) return tnCurves[0].curve;
    if (timeFrac >= 1) return tnCurves[tnCurves.length - 1].curve;
    // Find the two snapshots bracketing the slider position.
    const exact = timeFrac * (tnCurves.length - 1);
    const lo = Math.floor(exact);
    const hi = Math.min(tnCurves.length - 1, lo + 1);
    const t = exact - lo;
    const a = tnCurves[lo].curve;
    const b = tnCurves[hi].curve;
    if (!a || !b || a.length !== b.length) return a || todayCurve;
    return a.map((pt, i) => ({
      price: pt.price,
      pnl: pt.pnl * (1 - t) + (b[i]?.pnl ?? pt.pnl) * t,
    }));
  }, [tnCurves, timeFrac, todayCurve]);

  const activeDaysForward = useMemo(() => {
    if (!tnCurves || !tnCurves.length) return null;
    const last = tnCurves[tnCurves.length - 1];
    return timeFrac * (last.days_forward || 0);
  }, [tnCurves, timeFrac]);

  // Shocked spot price + P&L lookup against the active curve. We pick the
  // nearest grid point to avoid showing exact zeros when between samples.
  const shockedSpot = spot ? spot * (1 + shockPct) : null;
  const shockPnl = useMemo(() => {
    if (shockedSpot == null) return null;
    const arr = activeTodayCurve;
    if (!arr || !arr.length) return null;
    let best = arr[0];
    let bestDist = Math.abs(best.price - shockedSpot);
    for (let i = 1; i < arr.length; i++) {
      const d = Math.abs(arr[i].price - shockedSpot);
      if (d < bestDist) { best = arr[i]; bestDist = d; }
    }
    return best.pnl;
  }, [activeTodayCurve, shockedSpot]);

  const data = useMemo(
    () => mergeCurves(curve, activeTodayCurve, volMap, cone, spot, ivShockVp, vega),
    [curve, activeTodayCurve, volMap, cone, spot, ivShockVp, vega],
  );

  // Denominator for the P/L % view. Prefer defined max loss; fall back to
  // |net premium paid| or — for undefined-loss positions — the largest
  // absolute P&L sample on the curve (so the toggle still produces a
  // meaningful percent reading).
  const riskBasis = useMemo(() => {
    if (maxLoss != null && Math.abs(maxLoss) > 0) return Math.abs(maxLoss);
    if (netPremium != null && Math.abs(netPremium) > 0) return Math.abs(netPremium);
    if (!data.length) return 0;
    let m = 0;
    for (const d of data) {
      if (Math.abs(d.pnl) > m) m = Math.abs(d.pnl);
    }
    return m;
  }, [maxLoss, netPremium, data]);
  const showPct = pnlMode === "percent" && riskBasis > 0;
  const hasProbCurve = data.some((d) => d.prob > 0);

  const fullMin = data[0]?.price;
  const fullMax = data[data.length - 1]?.price;
  const activeMin = xDomain?.[0] ?? fullMin;
  const activeMax = xDomain?.[1] ?? fullMax;
  const fullSpan = (fullMax || 0) - (fullMin || 0);
  const activeSpan = (activeMax || 0) - (activeMin || 0);
  const zoomLevel = activeSpan > 0 ? fullSpan / activeSpan : 1;

  // Reset zoom whenever the underlying curve identity changes (new build).
  useEffect(() => {
    setXDomain(null);
  }, [curve]);

  const applyZoom = (factor, anchorPrice) => {
    if (!data.length || !fullMin || !fullMax) return;
    const lo = activeMin;
    const hi = activeMax;
    const center = anchorPrice != null && anchorPrice >= lo && anchorPrice <= hi
      ? anchorPrice
      : (lo + hi) / 2;
    let newLo = center - (center - lo) * factor;
    let newHi = center + (hi - center) * factor;
    // Clamp to full range
    if (newLo < fullMin) newLo = fullMin;
    if (newHi > fullMax) newHi = fullMax;
    const newSpan = newHi - newLo;
    // Minimum zoom = 2% of full range (don't zoom further than ~50x)
    if (newSpan < fullSpan * 0.02) return;
    // If we're back to ~full range, return to auto mode
    if (newSpan >= fullSpan * 0.998) {
      setXDomain(null);
    } else {
      setXDomain([newLo, newHi]);
    }
  };

  // Attach non-passive wheel listeners so we can preventDefault on the page
  // scroll while still bubbling our zoom logic.
  useEffect(() => {
    const attach = (host) => {
      if (!host) return () => {};
      const handler = (e) => {
        if (!data.length) return;
        // Only intercept vertical wheels — let trackpad horizontal scroll pass
        if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return;
        e.preventDefault();
        const factor = e.deltaY < 0 ? 0.85 : 1.18;
        applyZoom(factor, hoverPriceRef.current);
      };
      host.addEventListener("wheel", handler, { passive: false });
      return () => host.removeEventListener("wheel", handler);
    };
    const off1 = attach(wheelHostRef.current);
    const off2 = attach(fsWheelHostRef.current);
    return () => {
      off1();
      off2();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, activeMin, activeMax, expanded]);

  // Esc closes the fullscreen overlay no matter where focus lives.
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setExpanded(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [expanded]);

  if (!curve || !curve.length) {
    return (
      <div
        className="bg-[#0a0a0c] border border-white/10 rounded-xl p-6 h-[340px] flex items-center justify-center text-white/40 text-sm"
        data-testid="payoff-chart-empty"
      >
        Adjust sliders to render payoff
      </div>
    );
  }

  const Tip = buildTooltip(spot, pnlMode, riskBasis);

  const yTickFmt = (v) => {
    if (showPct) return `${((v / riskBasis) * 100).toFixed(0)}%`;
    return fmtPnL(v);
  };

  const renderChart = (height = 320) => (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart
        data={data}
        margin={{ top: 10, right: 18, bottom: 10, left: 0 }}
        onClick={(state) => {
          if (!interactive || !onAddLeg) return;
          const price = state?.activeLabel;
          if (typeof price !== "number") return;
          const x = state?.activeCoordinate?.x ?? 0;
          const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
          setPickerAt({ price: Math.round(price * 100) / 100, x, y: 28, isMobile });
        }}
        onMouseMove={(s) => {
          // Recharts gives us the active payload's price — stash it so the
          // wheel zoom can use cursor position as the zoom anchor.
          const p = s?.activePayload?.[0]?.payload?.price;
          hoverPriceRef.current = p ?? null;
        }}
        onMouseLeave={() => {
          hoverPriceRef.current = null;
        }}
      >
        <defs>
          <linearGradient id="profitFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00C805" stopOpacity={0.5} />
            <stop offset="100%" stopColor="#00C805" stopOpacity={0.04} />
          </linearGradient>
          <linearGradient id="lossFill" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="#FF3B30" stopOpacity={0.5} />
            <stop offset="100%" stopColor="#FF3B30" stopOpacity={0.04} />
          </linearGradient>
          <linearGradient id="probFill" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="#A98CFF" stopOpacity={0.22} />
            <stop offset="100%" stopColor="#A98CFF" stopOpacity={0.0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#ffffff10" />
        <XAxis
          dataKey="price"
          tick={{ fill: "#ffffff70", fontSize: 11 }}
          tickFormatter={(v) => `$${Math.round(v)}`}
          stroke="#ffffff30"
          type="number"
          domain={xDomain || ["dataMin", "dataMax"]}
          allowDataOverflow
        />
        <YAxis
          tick={{ fill: "#ffffff70", fontSize: 11 }}
          tickFormatter={yTickFmt}
          stroke="#ffffff30"
          width={70}
        />
        {hasProbCurve && showProb && (
          <YAxis
            yAxisId="prob"
            orientation="right"
            tick={false}
            axisLine={false}
            tickLine={false}
            domain={[0, 4]}
            width={0}
          />
        )}
        {hasVolume && showVolume && (
          <YAxis
            yAxisId="volume"
            orientation="right"
            tick={false}
            axisLine={false}
            tickLine={false}
            domain={[0, "dataMax"]}
            width={0}
          />
        )}
        <Tooltip content={<Tip />} cursor={{ stroke: "#ffffff40", strokeWidth: 1, strokeDasharray: "2 2" }} />
        <ReferenceLine y={0} stroke="#ffffff50" strokeDasharray="2 3" />
        {hasVolume && showVolume && (
          <Bar
            yAxisId="volume"
            dataKey="volume"
            fill="#A98CFF"
            fillOpacity={0.22}
            stroke="none"
            isAnimationActive={false}
            barSize={6}
            shape={(props) => {
              const { x, y, width, height, payload, fill, fillOpacity } = props;
              // Bar shape callback gives us per-datum payload; tag each rect
              // with a stable testid so playwright can assert bar counts.
              const strike = payload?.strike ?? payload?.x ?? "";
              return (
                <rect
                  x={x}
                  y={y}
                  width={width}
                  height={Math.max(0, height || 0)}
                  fill={fill}
                  fillOpacity={fillOpacity}
                  data-testid={`payoff-volume-bar-${strike}`}
                  data-volume={payload?.volume ?? 0}
                />
              );
            }}
          />
        )}
        {showCone && cone?.sigma_lo != null && cone?.sigma_hi != null && (
          <ReferenceArea
            x1={cone.sigma_lo}
            x2={cone.sigma_hi}
            fill="#A98CFF"
            fillOpacity={0.07}
            stroke="#A98CFF"
            strokeOpacity={0.35}
            strokeDasharray="3 3"
            label={{
              value: `~68% by ${cone.dte}d`,
              fill: "#A98CFF",
              fontSize: 9,
              position: "insideTop",
            }}
          />
        )}
        {spot != null && (
          <ReferenceLine
            x={spot}
            stroke="#FFD60A"
            strokeWidth={1.5}
            label={{ value: `Spot $${Number(spot).toFixed(0)}`, fill: "#FFD60A", fontSize: 10, position: "top" }}
          />
        )}
        {/* Shock scenario marker — vertical line at spot*(1+shockPct) when
            the user has dragged the shock slider away from 0. */}
        {shockedSpot != null && Math.abs(shockPct) > 0.001 && (
          <ReferenceLine
            x={shockedSpot}
            stroke="#FF7A33"
            strokeWidth={1.5}
            strokeDasharray="2 2"
            label={{
              value: `Shock ${shockPct >= 0 ? "+" : ""}${(shockPct * 100).toFixed(1)}% → $${shockedSpot.toFixed(2)} · ${shockPnl >= 0 ? "+" : ""}$${Math.round(shockPnl)}`,
              fill: "#FF7A33", fontSize: 10, position: "insideTopRight",
            }}
          />
        )}
        {breakevens.map((be, i) => (
          <ReferenceLine
            key={`be-${i}`}
            x={be}
            stroke="#00E5FF"
            strokeDasharray="3 3"
            label={{ value: `BE ${fmt$(be)}`, fill: "#00E5FF", fontSize: 9, position: "bottom" }}
          />
        ))}
        <Area type="monotone" dataKey="profit" stroke="none" fill="url(#profitFill)" isAnimationActive={false} />
        <Area type="monotone" dataKey="loss" stroke="none" fill="url(#lossFill)" isAnimationActive={false} />
        {hasProbCurve && showProb && (
          <Area
            yAxisId="prob"
            type="monotone"
            dataKey="prob"
            stroke="#A98CFF"
            strokeOpacity={0.45}
            strokeWidth={1}
            fill="url(#probFill)"
            isAnimationActive={false}
            name="Probability"
            connectNulls
          />
        )}
        <Line
          type="monotone"
          dataKey="pnl"
          stroke="#ffffffcc"
          strokeWidth={1.75}
          dot={false}
          isAnimationActive={false}
          name="At expiration"
        />
        {showToday && (
          <Line
            type="monotone"
            dataKey="today"
            stroke="#00E5FF"
            strokeWidth={1.5}
            strokeDasharray="4 3"
            dot={false}
            isAnimationActive={false}
            name={activeDaysForward != null
              ? `T+${Math.round(activeDaysForward)}d`
              : "Today (T+0)"}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );

  const headerRow = (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-3 px-1">
      <div>
        <div className="text-[9px] uppercase tracking-[0.24em] text-white/40">Risk Profile</div>
        <div className="text-base font-medium text-white mt-0.5">
          P&amp;L at Expiration{" "}
          {pop != null && (
            <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[#A98CFF]">
              POP {pop}%
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-[0.16em]">
        <span className="text-white/60">
          Max Profit:{" "}
          <span className="num text-[#00C805] font-medium" data-testid="payoff-max-profit">
            {maxProfit == null
              ? "∞"
              : (showPct ? fmtPnLPct((maxProfit / riskBasis) * 100) : fmtPnL(maxProfit))}
          </span>
        </span>
        <span className="text-white/60">
          Max Loss:{" "}
          <span className="num text-[#FF3B30] font-medium" data-testid="payoff-max-loss">
            {maxLoss == null
              ? "−∞"
              : (showPct ? fmtPnLPct((maxLoss / riskBasis) * 100) : fmtPnL(maxLoss))}
          </span>
        </span>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          data-testid="payoff-chart-expand"
          className="text-white/55 hover:text-white inline-flex items-center gap-1 border border-white/15 hover:border-white/35 rounded px-1.5 py-0.5 transition"
          title="Expand chart"
        >
          <Maximize2 className="w-3 h-3" /> Expand
        </button>
      </div>
    </div>
  );

  const togglesRow = (
    <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] mb-2 px-1 flex-wrap">
      {!externalPnlMode && (
        <button
          type="button"
          onClick={() => setPnlMode((m) => (m === "dollars" ? "percent" : "dollars"))}
          data-testid="payoff-toggle-pnl-mode"
          title={riskBasis > 0 ? "Switch P/L view between $ and % of risk" : "P/L % requires defined risk"}
          disabled={riskBasis <= 0}
          className={`px-2 py-0.5 rounded border transition inline-flex items-center gap-1 ${
            showPct
              ? "border-[#FFD60A]/50 text-[#FFD60A] bg-[#FFD60A]/8"
              : "border-white/15 text-white/55 hover:text-white/85 hover:border-white/30"
          } ${riskBasis <= 0 ? "opacity-40 cursor-not-allowed" : ""}`}
        >
          P/L {showPct ? "%" : "$"}
        </button>
      )}
      <button
        type="button"
        onClick={() => setShowToday((v) => !v)}
        data-testid="payoff-toggle-today"
        className={`px-2 py-0.5 rounded border transition ${
          showToday
            ? "border-[#00E5FF]/50 text-[#00E5FF] bg-[#00E5FF]/5"
            : "border-white/15 text-white/45 hover:text-white/70"
        }`}
      >
        T+0 Today
      </button>
      <button
        type="button"
        onClick={() => setShowCone((v) => !v)}
        data-testid="payoff-toggle-cone"
        className={`px-2 py-0.5 rounded border transition ${
          showCone
            ? "border-[#A98CFF]/50 text-[#A98CFF] bg-[#A98CFF]/5"
            : "border-white/15 text-white/45 hover:text-white/70"
        }`}
      >
        1σ cone
      </button>
      {hasProbCurve && (
        <button
          type="button"
          onClick={() => setShowProb((v) => !v)}
          data-testid="payoff-toggle-prob"
          title="Lognormal probability bell-curve at the dominant DTE"
          className={`px-2 py-0.5 rounded border transition ${
            showProb
              ? "border-[#A98CFF]/50 text-[#A98CFF] bg-[#A98CFF]/5"
              : "border-white/15 text-white/45 hover:text-white/70"
          }`}
        >
          Bell curve
        </button>
      )}
      {hasVolume && (
        <button
          type="button"
          onClick={() => setShowVolume((v) => !v)}
          data-testid="payoff-toggle-volume"
          title="Most-active strikes overlay"
          className={`px-2 py-0.5 rounded border transition inline-flex items-center gap-1 ${
            showVolume
              ? "border-[#A98CFF]/50 text-[#A98CFF] bg-[#A98CFF]/5"
              : "border-white/15 text-white/45 hover:text-white/70"
          }`}
        >
          <BarChart3 className="w-3 h-3" /> Volume
        </button>
      )}
      {cone?.dte != null && (
        <span className="text-white/35 normal-case tracking-normal">
          {cone.dte}d horizon
        </span>
      )}
      {/* Zoom controls — wheel-zoom on the chart itself, +/− here, reset
          appears once the user is no longer at the default view. */}
      <span className="text-white/20">·</span>
      <div className="flex items-center gap-1" data-testid="payoff-zoom-controls">
        <button
          type="button"
          onClick={() => applyZoom(0.7, hoverPriceRef.current ?? spot)}
          data-testid="payoff-zoom-in"
          title="Zoom in (or scroll-wheel up on chart)"
          className="p-1 rounded border border-white/15 text-white/55 hover:text-white hover:border-white/35 transition"
        >
          <ZoomIn className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={() => applyZoom(1.3, hoverPriceRef.current ?? spot)}
          data-testid="payoff-zoom-out"
          title="Zoom out (or scroll-wheel down on chart)"
          className="p-1 rounded border border-white/15 text-white/55 hover:text-white hover:border-white/35 transition"
        >
          <ZoomOut className="w-3 h-3" />
        </button>
        {xDomain && (
          <>
            <button
              type="button"
              onClick={() => setXDomain(null)}
              data-testid="payoff-zoom-reset"
              title="Reset zoom"
              className="p-1 rounded border border-[#FFD60A]/40 text-[#FFD60A] hover:bg-[#FFD60A]/10 transition"
            >
              <RotateCcw className="w-3 h-3" />
            </button>
            <span
              className="text-[#FFD60A] normal-case tracking-normal num"
              data-testid="payoff-zoom-level"
            >
              {zoomLevel.toFixed(1)}×
            </span>
          </>
        )}
      </div>
      {/* Inline zoom slider — better UX for trackpad / touch users. */}
      {!!data.length && (
        <div className="flex items-center gap-1.5 flex-1 min-w-[140px] max-w-[260px]">
          <span className="text-white/35 normal-case tracking-normal text-[9px]">1×</span>
          <input
            type="range"
            min={1}
            max={50}
            step={0.5}
            value={Math.min(50, zoomLevel)}
            data-testid="payoff-zoom-slider"
            onChange={(e) => {
              const target = parseFloat(e.target.value);
              if (!fullSpan) return;
              if (target <= 1.01) {
                setXDomain(null);
                return;
              }
              const newSpan = fullSpan / target;
              const center = hoverPriceRef.current ?? spot ?? (fullMin + fullMax) / 2;
              let lo = center - newSpan / 2;
              let hi = center + newSpan / 2;
              if (lo < fullMin) {
                hi += fullMin - lo;
                lo = fullMin;
              }
              if (hi > fullMax) {
                lo -= hi - fullMax;
                hi = fullMax;
              }
              setXDomain([Math.max(fullMin, lo), Math.min(fullMax, hi)]);
            }}
            className="w-full accent-[#A98CFF] h-1"
          />
          <span className="text-white/35 normal-case tracking-normal text-[9px]">50×</span>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div
        ref={wheelHostRef}
        className="bg-[#0a0a0c] border border-white/10 rounded-xl p-3 sm:p-5 relative"
        data-testid="payoff-chart"
      >
        {headerRow}
        {togglesRow}
        <div className="relative">
          {renderChart(320)}
          {interactive && (
            <InteractiveLegOverlay
              legs={legs}
              activeMin={activeMin}
              activeMax={activeMax}
              spot={spot}
              chartHeight={320}
              listedStrikes={snapToListed ? listedStrikes : null}
              onLegStrikeChange={onLegStrikeChange}
              onLegRemove={onLegRemove}
              draggingLegRef={draggingLegRef}
              setDragLive={setDragLive}
              focusedLegIdx={focusedLegIdx}
              setFocusedLegIdx={setFocusedLegIdx}
            />
          )}
          {dragLive && (
            <DragLivePill clientX={dragLive.clientX} clientY={dragLive.clientY} strike={dragLive.strike} />
          )}
          {pickerAt && (
            <LegPickerPopover
              price={pickerAt.price}
              x={pickerAt.x}
              y={pickerAt.y}
              isMobile={pickerAt.isMobile}
              onPick={(action, type) => {
                onAddLeg?.(pickerAt.price, action, type);
                setPickerAt(null);
              }}
              onClose={() => setPickerAt(null)}
            />
          )}
        </div>
        {/* T+N time slider + spot shock slider — OptionStrat-style scenario
            controls. Visible inline and also inside the expanded fullscreen
            chart for power-user inspection. */}
        {(tnCurves && tnCurves.length > 1) && !hideInlineScenario && (
          <ScenarioSliders
            timeFrac={timeFrac}
            setTimeFrac={setTimeFrac}
            shockPct={shockPct}
            setShockPct={setShockPct}
            ivShockVp={ivShockVp}
            setIvShockVp={setIvShockVp}
            vega={vega}
            tnCurves={tnCurves}
            spot={spot}
            shockedSpot={shockedSpot}
            shockPnl={shockPnl}
            activeDaysForward={activeDaysForward}
          />
        )}
        {interactive && (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[10.5px] text-white/40 italic leading-snug flex-1 min-w-0">
              <span className="text-[#FF7A33]">Click anywhere</span> on the chart to
              add a leg · drag handles to adjust · keyboard ← → nudge a focused leg ·
              click ✕ on a label to remove.
            </p>
            {listedStrikes?.length > 0 && (
              <button
                type="button"
                onClick={() => setSnapToListed((v) => !v)}
                data-testid="payoff-snap-strikes-toggle"
                title={snapToListed
                  ? "Snap to listed strikes (currently ON)"
                  : "Free drag — click to snap to listed strikes"}
                className={
                  "text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded transition shrink-0 inline-flex items-center gap-1 " +
                  (snapToListed
                    ? "bg-[#A98CFF]/15 text-[#A98CFF] border border-[#A98CFF]/45"
                    : "border border-white/15 text-white/55 hover:text-white hover:border-white/30")
                }
              >
                {snapToListed ? "🧲 Snap · listed strikes" : "Free drag"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Fullscreen modal overlay — Esc / X to close */}
      {expanded && (
        <div
          className="fixed inset-0 z-50 bg-[#050505]/90 backdrop-blur-sm flex flex-col p-4 sm:p-8"
          data-testid="payoff-chart-fullscreen"
          onClick={(e) => {
            if (e.target === e.currentTarget) setExpanded(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setExpanded(false);
          }}
          tabIndex={-1}
        >
          <div
            ref={fsWheelHostRef}
            className="max-w-[1400px] w-full mx-auto bg-[#0a0a0c] border border-white/10 rounded-xl p-4 sm:p-6 flex-1 flex flex-col"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>{headerRow}</div>
              <button
                type="button"
                onClick={() => setExpanded(false)}
                data-testid="payoff-chart-close"
                className="text-white/60 hover:text-white p-1 rounded border border-white/15 hover:border-white/40 transition"
                title="Close (Esc)"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {togglesRow}
            <div className="flex-1 min-h-0">{renderChart(Math.min(window.innerHeight - 220, 720))}</div>
            {(tnCurves && tnCurves.length > 1) && (
              <div className="mt-3">
                <ScenarioSliders
                  timeFrac={timeFrac}
                  setTimeFrac={setTimeFrac}
                  shockPct={shockPct}
                  setShockPct={setShockPct}
                  ivShockVp={ivShockVp}
                  setIvShockVp={setIvShockVp}
                  vega={vega}
                  tnCurves={tnCurves}
                  spot={spot}
                  shockedSpot={shockedSpot}
                  shockPnl={shockPnl}
                  activeDaysForward={activeDaysForward}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PayoffChart;
